import { preventBubblingEvent } from '@mote/helpers/browser'
import { KeyboardKey } from '@mote/helpers/constants'
import React from 'react'
import styled, { css } from 'styled-components'
import { Spinner } from '../spinner/spinner'

const spinnerLoadingColor = {
  primary: 'white',
  'primary-inverted': '#65008A',
  secondary: '#232323',
  ghost: '#232323',
  link: '#ac0ae8',
  'link-inverted': 'white'
}

export const Button = React.forwardRef(
  (
    {
      variant = 'primary',
      text,
      icon: _icon,
      htmlType = 'button',
      className = '',
      disabled = false,
      iconPosition = 'left',
      size = 'sm',
      loading = false,
      loadingText,
      block = false,
      autoWidth = false,
      style = {},
      tag = 'button',
      testId,
      as,
      ...props
    },
    ref
  ) => {
    const isOnlyIcon = React.useMemo(
      () => Boolean(_icon) && !(Boolean(text) || Boolean(loadingText)),
      [_icon, text, loadingText]
    )

    const spinner = React.useMemo(() => {
      return (
        <Spinner
          className='button-loading__spinner'
          color={spinnerLoadingColor[variant] ?? '#FFFFFF'}
          radius={24}
        />
      )
    }, [variant])

    const icon = React.useMemo(() => {
      return (
        <IconContainer
          iconPosition={iconPosition}
          isOnlyIcon={isOnlyIcon}
          size={size}
          className='button-icon'
        >
          {_icon}
        </IconContainer>
      )
    }, [_icon, iconPosition, isOnlyIcon, size])
    return (
      <Root
        ref={ref}
        as={as || tag}
        disabled={disabled || loading}
        variant={variant}
        type={[as, tag].includes('button') ? htmlType : undefined}
        className={className}
        size={size}
        block={block}
        autoWidth={autoWidth}
        style={style}
        data-testid={testId}
        {...props}
      >
        {loading && iconPosition === 'left' && <>{spinner}</>}
        {!loading && _icon && iconPosition === 'left' && <>{icon}</>}
        {!isOnlyIcon && (
          <Text
            className='button-text'
            variant={variant}
            size={size}
            $loading={loading}
            iconPosition={iconPosition}
            style={style?.text}
          >
            {loading && loadingText ? loadingText : text}
          </Text>
        )}
        {!loading && _icon && iconPosition === 'right' && <>{icon}</>}
        {loading && iconPosition === 'right' && <>{spinner}</>}
      </Root>
    )
  }
)

const Root = styled.button`
  display: inline-flex;
  flex: 0 0 auto;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  appearance: none;
  user-select: none;
  touch-action: manipulation;
  outline: transparent solid 2px !important;
  outline-offset: 2px;
  border-radius: 4px;
  border: none;
  width: auto;
  text-decoration: none;
  box-sizing: border-box;
  z-index: 1;
  white-space: nowrap;
  vertical-align: middle;
  font-family: 'DM Sans', sans-serif;
  transition: all 150ms ease-in;

  &:focus {
    box-shadow: 0 0 0 3px rgb(172, 10, 231, 0.4);
  }

  ${({ block, autoWidth }) => {
    if (block)
      return css`
        width: 100%;
      `

    if (autoWidth)
      return css`
        width: max-content;
      `
  }};

  ${({ size }) => {
    switch (size) {
      case 'lg':
        return css`
          padding: 0 20px;
          height: 48px;
        `
      default:
        return css`
          padding: 0 16px;
          height: 40px;
        `
    }
  }};

  ${({ variant }) =>
    variant === 'primary' &&
    css`
      background: #ac0ae8;
      span {
        color: white;
      }

      &:hover {
        background: #9b09d1;
      }

      &:active {
        background: #7807a2;
      }
    `};

  ${({ variant }) =>
    variant === 'primary-inverted' &&
    css`
      background: rgba(172, 10, 232, 0.1);

      &:hover {
        background: #9b09d1;
        span {
          color: white;
        }
      }

      &:active {
        background: #7807a2;

        span {
          color: white;
        }
      }
    `};

  ${({ variant }) =>
    variant === 'secondary' &&
    css`
      border: 1px solid rgba(213, 212, 218, 0.5);
      background: #ffffff;

      &:hover {
        background: rgba(213, 212, 218, 0.2);
      }

      &:active {
        border: 1px solid #232323;
      }
    `};

  ${({ variant }) =>
    variant === 'secondary-with-antithesis' &&
    css`
      border: 1px solid rgba(213, 212, 218, 0.5);
      background: #ffffff;

      &:hover {
        background: #9b09d1;
        border-color: #9b09d1;

        span {
          color: white;
        }
      }

      &:active {
        background: #7807a2;
        border: 1px solid #7807a2;

        span {
          color: white;
        }
      }
    `};

  ${({ variant }) =>
    variant === 'soft' &&
    css`
      background: rgba(213, 212, 218, 0.3);

      &:hover {
        background: rgba(213, 212, 218, 0.4);
      }

      &:active {
        background: rgba(213, 212, 218, 0.5);
      }
    `};

  ${({ variant }) =>
    variant === 'soft-inverted' &&
    css`
      background: rgba(255, 255, 255, 0.3);

      &:hover {
        background: rgba(255, 255, 255, 0.2);
      }

      &:active {
        background: rgba(255, 255, 255, 0.1);
      }
    `};

  ${({ variant }) =>
    variant === 'ghost' &&
    css`
      background: transparent;

      &:hover {
        background: rgba(213, 212, 218, 0.3);
      }

      &:active {
        background: rgba(213, 212, 218, 0.2);
      }
    `};

  ${({ variant }) =>
    variant === 'ghost-inverted' &&
    css`
      background: transparent;

      &:hover {
        background: rgba(255, 255, 255, 0.3);
      }

      &:active {
        background: rgba(255, 255, 255, 0.2);
      }
    `};

  ${({ variant }) =>
    variant === 'link' &&
    css`
      padding: 0;
      min-width: unset;
      background: unset;
      height: unset;
      justify-content: flex-start;

      span {
        color: #ac0ae8;
      }

      &:hover {
        span {
          color: #9b09d1;
        }
      }

      &:active {
        span {
          color: #7807a2;
        }
      }
    `};

  ${({ variant }) =>
    variant === 'link-inverted' &&
    css`
      padding: 0;
      color: white;
      min-width: unset;
      background: unset;
      height: unset;
      justify-content: flex-start;

      &:hover {
        span {
          color: rgba(255, 255, 255, 0.6);
        }
      }

      &:active {
        span {
          color: rgba(255, 255, 255, 0.3);
        }
      }
    `};

  ${({ variant }) =>
    variant === 'link-secondary' &&
    css`
      padding: 0;
      color: white;
      min-width: unset;
      background: unset;
      height: unset;
      justify-content: flex-start;

      span {
        color: #232323;
      }

      &:hover {
        span {
          color: #ac0ae8;
        }
      }

      &:active {
        span {
          color: #ac0ae8;
        }
      }
    `};

  &:focus {
    outline: none;
  }

  &[disabled] {
    cursor: not-allowed;
    opacity: 0.4;

    span {
      cursor: not-allowed;
    }
  }
`

const Text = styled.span`
  font-style: normal;
  font-weight: 400;
  color: #ffffff;
  line-height: 120%;
  text-align: center;

  ${({ $loading, iconPosition }) => {
    if (!$loading) return

    switch (iconPosition) {
      case 'left':
        return css`
          margin-left: 8px;
        `
      case 'right':
        return css`
          margin-right: 8px;
        `
    }
  }};

  ${({ size }) => {
    switch (size) {
      case 'lg':
        return css`
          font-size: 16px;
        `
      default:
        return css`
          font-size: 15px;
        `
    }
  }};

  ${({ variant }) =>
    variant === 'primary-inverted' &&
    css`
      color: #ac0ae8;
    `};

  ${({ variant }) =>
    variant === 'secondary' &&
    css`
      color: #232323;
    `};

  ${({ variant }) =>
    variant === 'secondary-with-antithesis' &&
    css`
      color: #232323;
    `};

  ${({ variant }) =>
    variant === 'soft' &&
    css`
      color: #232323;
    `};

  ${({ variant }) =>
    variant === 'soft-inverted' &&
    css`
      color: #ffffff;
    `};

  ${({ variant }) =>
    variant === 'ghost' &&
    css`
      color: #232323;
    `};

  ${({ variant }) =>
    variant === 'ghost-inverted' &&
    css`
      color: #ffffff;
    `};
`

const IconContainer = styled.span`
  img,
  svg {
    width: 100%;
    height: 100%;
  }

  ${({ iconPosition, isOnlyIcon }) =>
    !isOnlyIcon &&
    iconPosition === 'left' &&
    css`
      margin-right: 8px;
    `};

  ${({ iconPosition, isOnlyIcon }) =>
    !isOnlyIcon &&
    iconPosition === 'right' &&
    css`
      margin-left: 8px;
    `};

  ${({ size }) => {
    switch (size) {
      case 'lg':
        return css`
          height: 32px;
          width: 32px;
        `
      default:
        return css`
          height: 24px;
          width: 24px;
        `
    }
  }};
`
